import {useEffect, useState} from 'react'
import {getJwt} from '../../hooks/useJwt'
import {jwtGetFetcher, jwtPostFetcher} from '../../utils/authFetch'
import {REVIEWER_TYPE_ADMIN} from '../../enums/UserRole'

export const EXPERT_TIME_HIDDEN = 'HIDDEN'
export const EXPERT_TIME_OPTIONAL = 'OPTIONAL'
export const EXPERT_TIME_REQUIRED = 'REQUIRED'

export const MAX_EXPERT_TIME_HOURS = 99
export const MAX_EXPERT_TIME_MINUTES = MAX_EXPERT_TIME_HOURS * 60 + 59

// Missing or unrecognised values behave as OPTIONAL, like the backend.
export const getExpertTimeMode = (providerInfoSec) =>
  [EXPERT_TIME_HIDDEN, EXPERT_TIME_OPTIONAL, EXPERT_TIME_REQUIRED].includes(providerInfoSec?.expertTimeMode)
    ? providerInfoSec.expertTimeMode
    : EXPERT_TIME_OPTIONAL

// Both fields empty means "never entered" (null); otherwise an empty field counts as 0.
export const toTotalMinutes = (hours, minutes) => {
  if ((hours ?? '') === '' && (minutes ?? '') === '') return null
  return (Number(hours) || 0) * 60 + (Number(minutes) || 0)
}

export const splitMinutes = (total) => total === null || total === undefined
  ? {hours: '', minutes: ''}
  : {hours: String(Math.floor(total / 60)), minutes: String(total % 60)}

export const EXPERT_TIME_ERROR_REQUIRED = 'required'
export const EXPERT_TIME_ERROR_RANGE = 'range'

export const validateExpertTime = (total, required) => {
  if (total === null || total === undefined) return required ? EXPERT_TIME_ERROR_REQUIRED : null
  return Number.isInteger(total) && total >= 0 && total <= MAX_EXPERT_TIME_MINUTES ? null : EXPERT_TIME_ERROR_RANGE
}

export const EXPERT_ROLE_PRIMARY = 'PRIMARY'
export const EXPERT_ROLE_ADDITIONAL = 'ADDITIONAL'

const reviewerOf = (entry) => ({reviewerType: entry.reviewerType, reviewerId: entry.reviewerId ?? entry.expertId})

const reviewerQuery = (entry) => {
  const {reviewerType, reviewerId} = reviewerOf(entry)
  return `&reviewerType=${encodeURIComponent(reviewerType)}&reviewerId=${encodeURIComponent(reviewerId)}`
}

const isSameReviewer = (a, b) => {
  const [x, y] = [reviewerOf(a), reviewerOf(b)]
  return x.reviewerType === y.reviewerType && x.reviewerId === y.reviewerId
}

export const isOwnExpertTimeEntry = (entry, userInfo) => entry.reviewerType === REVIEWER_TYPE_ADMIN && entry.reviewerId === userInfo?.id

// `entry` is only passed when an admin saves the time of an expert; omit it to save the logged user's own.
export const saveExpertTime = (requestId, minutes, entry) =>
  jwtPostFetcher(getJwt())(
    `/api/caseExpertTime?requestId=${requestId}${entry ? reviewerQuery(entry) : ''}`,
    entry ? {minutes, ...reviewerOf(entry)} : {minutes},
    {method: 'PUT'}
  )

export const deleteExpertTime = (requestId, entry) =>
  jwtPostFetcher(getJwt())(`/api/caseExpertTime?requestId=${requestId}${reviewerQuery(entry)}`, reviewerOf(entry), {method: 'DELETE'})

// An expert only receives their own entry from GET /caseExpertTime.
export const useOwnExpertTime = (requestId) => {
  const [state, setState] = useState({isLoaded: false, entry: null})

  useEffect(() => {
    let active = true
    jwtGetFetcher(getJwt())(`/api/caseExpertTime?requestId=${requestId}`)
      .then((response) => active && setState({isLoaded: true, entry: response?.entries?.[0] || null}))
      .catch(() => active && setState({isLoaded: true, entry: null}))
    return () => { active = false }
  }, [requestId])

  const setEntry = (entry) => setState((s) => ({...s, entry: {...s.entry, ...entry}}))

  return {...state, setEntry}
}

const ROLE_RANK = {[EXPERT_ROLE_PRIMARY]: 0}

// An admin receives the entry of every expert that has a time (Primary and Secondary), in assignment order.
export const useExpertTimeEntries = (requestId) => {
  const [state, setState] = useState({isLoaded: false, entries: []})

  useEffect(() => {
    let active = true
    jwtGetFetcher(getJwt())(`/api/caseExpertTime?requestId=${requestId}`)
      .then((response) => active && setState({isLoaded: true, entries: sortExpertTimeEntries(response?.entries)}))
      .catch(() => active && setState({isLoaded: true, entries: []}))
    return () => { active = false }
  }, [requestId])

  const updateEntry = (target, patch) => setState((s) => ({
    ...s,
    entries: s.entries.map((entry) => isSameReviewer(entry, target) ? {...entry, ...patch} : entry)
  }))

  return {...state, updateEntry}
}

export const sortExpertTimeEntries = (entries = []) => entries
  .filter((entry) => entry.role !== EXPERT_ROLE_ADDITIONAL)
  .sort((a, b) => (ROLE_RANK[a.role] ?? 1) - (ROLE_RANK[b.role] ?? 1) || (a.order ?? 0) - (b.order ?? 0))

export const sumExpertTimes = (minutesList) => minutesList.reduce((total, minutes) => total + (minutes ?? 0), 0)
